"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
var _toConsumableArray2 = _interopRequireDefault(require("@babel/runtime/helpers/toConsumableArray"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _jsTools = require("@airportlabs/js-tools");
var _react = require("react");
/**
 *  Executes a queue of jobs in batches
 *  Job execution is debounced, but has a max debounce time
 *  Individual jobs are executed immediately, consecutive jobs are executed in batches
 *
 * @param {func} setData - A callback called when data is ready to be set
 * @param {object} options - { name - of the client, debounceMs, maxDebounceMs }
 * @returns {array} - [A function to push data to the queue, A boolean indicating if the queue is being flushed]
 */
var useDebouncedOperation = function useDebouncedOperation(_ref) {
  var setExternalData = _ref.setData,
    _ref$name = _ref.name,
    name = _ref$name === void 0 ? "Unnamed" : _ref$name,
    _ref$debounceMs = _ref.debounceMs,
    debounceMs = _ref$debounceMs === void 0 ? 300 : _ref$debounceMs,
    _ref$maxDebounceMs = _ref.maxDebounceMs,
    maxDebounceMs = _ref$maxDebounceMs === void 0 ? 3000 : _ref$maxDebounceMs,
    onFlush = _ref.onFlush,
    dependencies = _ref.dependencies,
    _ref$debug = _ref.debug,
    debug = _ref$debug === void 0 ? false : _ref$debug;
  var queue = (0, _react.useRef)([]);
  var localData = (0, _react.useRef)([]);
  var timeout = (0, _react.useRef)();
  var lastQueueFlush = (0, _react.useRef)(Date.now());
  var _useState = (0, _react.useState)(false),
    _useState2 = (0, _slicedToArray2["default"])(_useState, 2),
    isFlushingQueue = _useState2[0],
    setIsFlushingQueue = _useState2[1];
  var graphqlConsole = (0, _jsTools.getGraphqlConsole)(debug);
  var setData = (0, _react.useCallback)(function (newData) {
    // Jobs may modify the localData.current directly in a non-immutable way,
    // so we need to make sure updating the external data always generates a
    // render.
    setExternalData((0, _toConsumableArray2["default"])(newData));
  }, [setExternalData]);

  // setData was changed, call it
  (0, _react.useEffect)(function () {
    setData(localData.current);
  }, [setData]);

  // Processes the queue by executing each job and updating the data.
  var flush = (0, _react.useCallback)(function () {
    setIsFlushingQueue(true);

    // Size of queue saved as the queue may be modified during the loop
    var currentQueueCount = queue.current.length;
    if (currentQueueCount === 0) {
      setIsFlushingQueue(false);
      lastQueueFlush.current = Date.now();
      return;
    }

    // Execute each job in the queue (that were there when the loop started)
    for (var i = 0; i < currentQueueCount; i++) {
      var job = queue.current.shift();

      // Job called with current value and a setter that accepts a value or a
      // callback called with the current value.
      job(localData.current, function (newState) {
        localData.current = typeof newState === "function" ? newState(
        // Can be modified directly
        localData.current) : newState;
      });
    }
    setData(localData.current);
    onFlush === null || onFlush === void 0 || onFlush(localData.current);
    setIsFlushingQueue(false);
    lastQueueFlush.current = Date.now();
  }, [setData, onFlush]);
  var restartTimeout = (0, _react.useCallback)(function () {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(flush, debounceMs);
  }, [debounceMs, flush]);
  var pushToQueue = (0, _react.useCallback)(function (
  // A job is a callback called with the current state and a callback to set the next state
  job,
  // This job will only be added to the queue if it's not the first job in the queue
  shouldNotBeFirst) {
    // Fixes an issue in useSuspenseSubscription where sometimes the
    // subscription only returns READY and no data, in which case the READY
    // job isn't pushed in the queue and does not trigger a onFlush call
    // (which would update the query cache with an empty array).
    if (shouldNotBeFirst && !queue.current.length) {
      return;
    }
    queue.current.push(job);

    // Job can be done in the maxDebounceMs time frame
    // Elapsed time is less than maxDebounceMs - debounceMs
    if (Date.now() - lastQueueFlush.current + debounceMs < maxDebounceMs) {
      restartTimeout();
    }
    // Max debounce time has been reached, execute the queue immediately
    else {
      flush();
    }
  }, [debounceMs, maxDebounceMs, flush, restartTimeout]);
  var resetQueue = (0, _react.useCallback)(function () {
    graphqlConsole.log("Resetting queue", {
      queueLength: queue.current.length
    });
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    localData.current = [];
    setIsFlushingQueue(false);
    lastQueueFlush.current = Date.now();
  }, [graphqlConsole]);

  // Makes sure that the queue isn't flushed after is unmounted. The queue will
  // also be reset when the dependencies change. Using "JSON.stringify" to
  // compare value not reference.
  var dependenciesJSON = JSON.stringify(dependencies);
  (0, _react.useEffect)(function () {
    return function () {
      resetQueue();
    };
  }, [resetQueue, dependenciesJSON]);
  return [pushToQueue, {
    isFlushingQueue: isFlushingQueue,
    flush: flush,
    resetQueue: resetQueue
  }];
};
var _default = exports["default"] = useDebouncedOperation;